import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { useExpenseAccountOptions } from '@/features/accounts/api';
import { useOrgUsers } from '@/features/admin/api';

/** The four header boxes, as the editors hold them (blank = not set). */
export interface ReturnHeaderValues {
  manualRef: string;
  bookNo: string;
  buyerUserId: string;
  returnsAccountId: string;
}

export const EMPTY_RETURN_HEADER: ReturnHeaderValues = {
  manualRef: '',
  bookNo: '',
  buyerUserId: '',
  returnsAccountId: '',
};

/**
 * Wave 5D — the debit note's header fields, shared by the bill-linked and the
 * direct editor: the supplier's own reference, the physical book number, the
 * buyer (one of the company's users) and the per-document returns account.
 *
 * The account picker offers EXPENSE leaves only, on purpose: the override
 * reaches the price-difference leg alone — never the payable, never
 * inventory — so an asset or liability account there would be a pick the
 * server refuses. Blank means the company's `purchasing.returns_account`
 * setting, else 5050.
 */
export function ReturnHeaderFields({
  values,
  onChange,
  editable,
  idPrefix,
}: {
  values: ReturnHeaderValues;
  onChange: (patch: Partial<ReturnHeaderValues>) => void;
  editable: boolean;
  idPrefix: string;
}) {
  const { t } = useTranslation();
  const { data: users } = useOrgUsers();
  const { options: accountOptions } = useExpenseAccountOptions();
  // A buyer who has since been deactivated stays selectable on the note that
  // names them; nobody new can be picked from the deactivated list.
  const buyers = (users ?? []).filter(
    (user) => user.status === 'active' || user.id === values.buyerUserId,
  );
  return (
    <>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-manual-ref`}>{t('preturn.fManualRef')}</Label>
        <Input
          id={`${idPrefix}-manual-ref`}
          dir="ltr"
          value={values.manualRef}
          maxLength={60}
          placeholder={t('preturn.manualRefPh')}
          onChange={(event) => onChange({ manualRef: event.target.value })}
          disabled={!editable}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-book-no`}>{t('preturn.fBookNo')}</Label>
        <Input
          id={`${idPrefix}-book-no`}
          dir="ltr"
          value={values.bookNo}
          maxLength={40}
          onChange={(event) => onChange({ bookNo: event.target.value })}
          disabled={!editable}
        />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-buyer`}>{t('preturn.fBuyer')}</Label>
        <NativeSelect
          id={`${idPrefix}-buyer`}
          value={values.buyerUserId}
          onChange={(event) => onChange({ buyerUserId: event.target.value })}
          disabled={!editable}
        >
          <option value="">{t('preturn.noBuyer')}</option>
          {buyers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name}
            </option>
          ))}
        </NativeSelect>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={`${idPrefix}-returns-account`}>{t('preturn.fReturnsAccount')}</Label>
        <NativeSelect
          id={`${idPrefix}-returns-account`}
          value={values.returnsAccountId}
          onChange={(event) => onChange({ returnsAccountId: event.target.value })}
          disabled={!editable}
        >
          <option value="">{t('preturn.returnsAccountDefault')}</option>
          {accountOptions.map((account) => (
            <option key={account.id} value={account.id}>
              {account.code} — {account.name}
            </option>
          ))}
        </NativeSelect>
        <p className="text-xs text-muted-foreground">{t('preturn.returnsAccountHint')}</p>
      </div>
    </>
  );
}
